import { AddBoxOutlined } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	Divider,
	IconButton,
	Link,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import React, { useCallback, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import HtmlEditor from "../../../UI/HtmlEditor";
import {
	useAllNotesOfSpecificApplication,
	useCreateApplicationNote
} from "../../../../hooks/useApplicationNote";
import { useApplicationNoteSubjects } from "../../../../hooks/useReferential";
import { useCurrentUser } from "../../../../hooks/useUser";
import { ApplicationNoteSubject } from "../../../../models/ApplicationNoteSubject";
import { zoullooColors } from "../../../../theme";
import { formatDate } from "../../../../utils/formatting";
import ApplicationNotesSkeleton from "./Components/ApplicationNotesSkeleton";
import ApplicationNotesErrorDisplay from "./Components/ApplicationNotesErrorDisplay";
import NoApplicationNotesFound from "./Components/NoApplicationNotesFound";
import { FreelancerProfile } from "../../../../models/FreelancerProfile";

interface ApplicationNotesProps {
	freelancerProfile: FreelancerProfile;
}

const ApplicationNotes: React.FC<ApplicationNotesProps> = ({ freelancerProfile }) => {
	const { t } = useTranslation();

	// Get current user for notes functionality
	const {
		data: currentUser,
		isLoading: isUserLoading,
		isError: isUserError,
		refetch: refetchUser
	} = useCurrentUser();

	// Fetch note subjects and notes
	const { data: noteSubjects = [] } = useApplicationNoteSubjects();
	const {
		data: notes = [],
		isLoading: isNotesLoading,
		isError: isNotesError,
		refetch: refetchNotes
	} = useAllNotesOfSpecificApplication(freelancerProfile.onboarding.id);
	const createNoteMutation = useCreateApplicationNote();

	// State for managing talent relationship tracking section
	const [isAddingNote, setIsAddingNote] = useState(false);
	const [noteExpanded, setNoteExpanded] = useState<{ [key: number]: boolean }>({});
	const [noteOverflows, setNoteOverflows] = useState<{ [key: number]: boolean }>({});
	const [newlyAddedNotes, setNewlyAddedNotes] = useState<Set<number>>(new Set());
	const [previousNotesLength, setPreviousNotesLength] = useState(0);

	// Ref for smooth scrolling to add mode section
	const addModeRef = useRef<HTMLDivElement>(null);

	const debouncedFormikUpdate = useRef<{ [key: string]: any }>({});

	// Track newly added notes for pulsing animation and scroll to them
	useEffect(() => {
		if (notes.length > previousNotesLength && previousNotesLength > 0) {
			// Get the newly added note (should be the first one since notes are ordered by creation date desc)
			const newNote = notes[0];
			if (newNote) {
				setNewlyAddedNotes(prev => new Set(prev).add(newNote.id));

				// Scroll to the newly added note after a short delay to ensure DOM is updated
				previousNotesLength > 4 &&
					setTimeout(() => {
						const noteElement = document.querySelector(`[data-note-id="${newNote.id}"]`);
						if (noteElement) {
							const elementRect = noteElement.getBoundingClientRect();
							const scrollTop = elementRect.top + window.pageYOffset - 300; // 300px offset for better visibility

							window.scrollTo({
								top: scrollTop,
								behavior: "smooth"
							});
						}
					}, 200);

				// Remove the note from newly added set after animation duration
				setTimeout(() => {
					setNewlyAddedNotes(prev => {
						const updated = new Set(prev);
						updated.delete(newNote.id);
						return updated;
					});
				}, 3000); // 3 seconds for the fade animation
			}
		}
		setPreviousNotesLength(notes.length);
	}, [notes, previousNotesLength]);

	// CSS styles for the fade animation
	const fadeAnimation = {
		"@keyframes fadeGold": {
			"0%": {
				backgroundColor: "rgba(252, 206, 3, 0.4)", // Start with golden-yellowish background
				transform: "scale(1)"
			},
			"100%": {
				backgroundColor: "transparent", // Fade to transparent
				transform: "scale(1)"
			}
		}
	};

	// Yup validation schema
	const validationSchema = yup.object({
		noteSubjectId: yup.number().required(t("freelancerDetails.notes.subjectRequired")),
		content: yup
			.string()
			.required(t("freelancerDetails.notes.contentRequired"))
			.max(4000, t("freelancerDetails.notes.contentMaxLength"))
	});

	// Formik form
	const formik = useFormik({
		initialValues: {
			noteSubjectId: null as number | null,
			content: ""
		},
		validationSchema,
		onSubmit: async values => {
			if (!currentUser?.id || !values.noteSubjectId) return;

			try {
				await createNoteMutation.mutateAsync({
					noteRequest: {
						content: values.content,
						freelancerOnboardingId: freelancerProfile.onboarding.id,
						applicationNoteSubjectId: values.noteSubjectId
					},
					userId: currentUser.id
				});

				// Reset form and close add mode
				formik.resetForm();
				setIsAddingNote(false);
			} catch (error) {
				console.error("Error creating note:", error);
			}
		}
	});

	// Function to toggle note expansion
	const onToggleNote = useCallback((noteId: number) => {
		setNoteExpanded(prev => ({
			...prev,
			[noteId]: !prev[noteId]
		}));
	}, []);

	// Function to handle add note button click with smooth scrolling
	const handleAddNoteClick = useCallback(() => {
		const newState = !isAddingNote;
		setIsAddingNote(newState);

		if (newState) {
			// Use multiple checks to ensure the element is available and DOM is ready
			const scrollToAddMode = () => {
				if (addModeRef.current) {
					// Get the element's position and add some offset for better visual positioning
					const elementRect = addModeRef.current.getBoundingClientRect();
					const scrollTop = elementRect.top + window.pageYOffset - 100; // 100px offset for better visibility

					window.scrollTo({
						top: scrollTop,
						behavior: "smooth"
					});
				} else {
					// If ref is not available yet, try again after a longer delay
					setTimeout(scrollToAddMode, 100);
				}
			};

			// Initial delay to ensure the DOM has updated
			setTimeout(scrollToAddMode, 150);
		}
	}, [isAddingNote]);

	// Function to set note ref and check for overflow
	const setNoteRef = useCallback((el: HTMLElement | null, noteId: number) => {
		if (el) {
			// Use requestAnimationFrame to ensure DOM has been updated
			requestAnimationFrame(() => {
				const hasOverflow = el.scrollHeight > el.clientHeight;
				setNoteOverflows(prev => ({
					...prev,
					[noteId]: hasOverflow
				}));
			});
		}
	}, []);

	// Handle cancel add note
	const handleCancelAddNote = useCallback(() => {
		formik.resetForm();
		setIsAddingNote(false);
	}, [formik]);

	const getDebouncedUpdate = useCallback((fieldPath: string) => {
		if (!debouncedFormikUpdate.current[fieldPath]) {
			let timeoutId: NodeJS.Timeout;
			debouncedFormikUpdate.current[fieldPath] = (value: string) => {
				clearTimeout(timeoutId);
				timeoutId = setTimeout(() => {
					formik.setFieldValue(fieldPath, value);
				}, 300);
			};
		}
		return debouncedFormikUpdate.current[fieldPath];
	}, []);

	// Function to render notes content
	const renderNotesContent = () => {
		if (isUserError) {
			return <ApplicationNotesErrorDisplay onRetry={refetchUser} />;
		}

		if (isNotesError) {
			return <ApplicationNotesErrorDisplay onRetry={refetchNotes} />;
		}

		if (notes.length === 0) {
			return <NoApplicationNotesFound />;
		}

		return (
			<Stack>
				{notes.map((note, index) => {
					const isNoteExpanded = noteExpanded[note.id] || false;
					const noteHasOverflow = noteOverflows[note.id] || false;
					const isNewlyAdded = newlyAddedNotes.has(note.id);

					return (
						<>
							<Box
								key={note.id}
								data-note-id={note.id}
								sx={{
									p: 2,
									...fadeAnimation,
									...(isNewlyAdded && {
										animation: "fadeGold 4s ease-out forwards"
									})
								}}
							>
								<Typography variant="subtitle1" fontWeight="medium" mb={0.5}>
									{(() => {
										// Translation key follows the convention "NoteSubjects.<subject Name>".
										// If no translation exists for this subject (e.g. dynamically-created
										// subjects like "Commentaire CSM sur le talent"), fall back to the
										// raw label coming from the database — better than showing the key.
										const baseLabel = t(
											`NoteSubjects.${note.subject.label}`,
											note.subject.label
										);
										// Special format for "Commentaire CSM sur le talent" linked to a project
										if (note.project) {
											const clientName =
												note.project.companyName || note.project.projectName;
											return `${baseLabel} - Projet ${clientName} - ${note.project.projectNumber}`;
										}
										return baseLabel;
									})()}
								</Typography>
								<Typography variant="body2" color="text.secondary" mb={0.5}>
									{formatDate(
										note.createdAt instanceof Date
											? note.createdAt.toISOString()
											: String(note.createdAt)
									)}
									,{" "}
									{(note.createdAt instanceof Date
										? note.createdAt
										: new Date(note.createdAt)
									).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
								</Typography>
								<Typography variant="body2" color="text.secondary" mb={1}>
									{note.authorFullName ||
										`${currentUser?.firstName ?? ""} ${currentUser?.lastName ?? ""}`.trim()}
								</Typography>
								<HtmlEditor
									ref={el => setNoteRef(el, note.id)}
									readonly={true}
									content={note.content}
									sx={{
										display: "-webkit-box",
										WebkitLineClamp: isNoteExpanded ? "none" : 2,
										WebkitBoxOrient: "vertical",
										overflow: "hidden",
										textOverflow: "ellipsis",
										marginBottom: 2
									}}
								/>

								{/* Read More/Less Link - show if content overflows OR is currently expanded */}
								{(noteHasOverflow || isNoteExpanded) && (
									<Link
										component="button"
										variant="caption"
										color="primary"
										onClick={(e: React.MouseEvent) => {
											e.preventDefault();
											e.stopPropagation();
											onToggleNote(note.id);
										}}
										sx={{
											textDecoration: "none",
											fontWeight: "medium",
											cursor: "pointer",
											border: "none",
											background: "none",
											padding: 0,
											"&:hover": {
												textDecoration: "underline"
											},
											display: "block",
											mt: 0.5,
											fontSize: "15px",
											width: "fit-content"
										}}
									>
										{isNoteExpanded
											? t("freelancerDetails.experience.readLess")
											: t("freelancerDetails.experience.readMore")}
									</Link>
								)}
							</Box>

							{index < notes.length - 1 && <Divider />}
						</>
					);
				})}
			</Stack>
		);
	};

	return (
		<>
			{isNotesLoading || isUserLoading ? (
				<ApplicationNotesSkeleton />
			) : (
				<Box
					m={2}
					sx={{
						boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
						border: `1px solid ${zoullooColors.grey[400]}`,
						borderRadius: 3
					}}
				>
					<Box
						sx={{
							p: 2,
							backgroundColor: zoullooColors.background.paper,
							borderBottom: `1px solid ${zoullooColors.grey[400]}`,
							borderTopLeftRadius: 13,
							borderTopRightRadius: 13,
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center"
						}}
					>
						<Typography variant="h6">
							{t("freelancerDetails.notes.talentRelationship")}
						</Typography>
						<IconButton
							disableRipple
							onClick={handleAddNoteClick}
							color="secondary"
							size="small"
							disabled={!currentUser?.id}
						>
							<AddBoxOutlined fontSize="large" />
						</IconButton>
					</Box>

					{/* Notes Display */}
					<Box
						sx={{
							backgroundColor: zoullooColors.background.paper,
							borderBottomLeftRadius: isAddingNote ? 0 : 13,
							borderBottomRightRadius: isAddingNote ? 0 : 13
						}}
					>
						{renderNotesContent()}
					</Box>

					{/* Add Mode - Shows below display mode when isAddingNote is true */}
					{isAddingNote && (
						<FormikProvider value={formik}>
							<Box
								ref={addModeRef}
								component="form"
								onSubmit={formik.handleSubmit}
								sx={{
									p: 2,
									borderTop: `1px solid ${zoullooColors.grey[400]}`,
									backgroundColor: zoullooColors.background.default,
									borderBottomLeftRadius: 13,
									borderBottomRightRadius: 13
								}}
							>
								<Box sx={{ mb: 2 }}>
									<Autocomplete
										options={noteSubjects}
										getOptionLabel={(option: ApplicationNoteSubject) =>
											t(`NoteSubjects.${option.label}`)
										}
										value={
											noteSubjects.find(
												subject => subject.id === formik.values.noteSubjectId
											) || null
										}
										onChange={(_, newValue) => {
											formik.setFieldValue("noteSubjectId", newValue?.id || null);
										}}
										onBlur={() => formik.setFieldTouched("noteSubjectId", true)}
										renderInput={params => (
											<TextField
												{...params}
												label={t("freelancerDetails.notes.subjectLabel")}
												placeholder={t("freelancerDetails.notes.subjectPlaceholder")}
												fullWidth
												error={
													formik.touched.noteSubjectId &&
													Boolean(formik.errors.noteSubjectId)
												}
												helperText={
													formik.touched.noteSubjectId && formik.errors.noteSubjectId
												}
											/>
										)}
										isOptionEqualToValue={(option, value) => option.id === value.id}
									/>
								</Box>

								<Box sx={{ mb: 2 }}>
									<HtmlEditor
										content={formik.values.content}
										onUpdate={({ editor }) => {
											getDebouncedUpdate("content")(editor.getHTML());
										}}
										onBlur={() => {
											formik.setFieldTouched("content", true);
										}}
										placeholder={t("freelancerDetails.notes.contentPlaceholder")}
									/>
									{formik.touched.content && formik.errors.content && (
										<Typography
											variant="body1"
											color="error"
											sx={{ mt: 1, display: "block" }}
										>
											{formik.errors.content}
										</Typography>
									)}
								</Box>

								<Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
									<Button
										type="submit"
										variant="contained"
										color="secondary"
										disabled={
											!validationSchema.isValidSync(formik.values) || formik.isSubmitting
										}
									>
										{t("freelancerDetails.notes.save")}
									</Button>
									<Button
										variant="outlined"
										color="secondary"
										onClick={handleCancelAddNote}
										disabled={formik.isSubmitting}
									>
										{t("freelancerDetails.notes.cancel")}
									</Button>
								</Box>
							</Box>
						</FormikProvider>
					)}
				</Box>
			)}
		</>
	);
};

export default ApplicationNotes;
